import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { removeItem, updateQty, clearOrderDiscount, setCustomer } from "../../../Redux/features/WebRegister/cartSlice";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { fmt } from "../uiUtils";

// Tax rate label — Android taxLabelString prints the rate AS STORED ("7.255%", "9.75%", "8%"), so a
// 3-decimal state rate is never rounded away and a whole-number rate gains no zeros.
const fmtPct = (raw, num) => (raw != null && String(raw).trim() !== "" ? String(raw).trim() : String(Number(num) || 0));

// Android: `customerModel.total_bonus_points ?: "0.00"` — the raw server string, shown as-is.
// ⚠️ The register's App/find_customer response actually names the balance `total_loyalty_pts`
// (verified live 2026-08-27: Abhi = "398.89"). That key was missing here, so the chip always read
// "0.00 Points" for a customer who HAD points. The Android model keys are kept as fallbacks.
const customerPointsLabel = (c) => {
  for (const k of ["total_loyalty_pts", "total_bonus_points", "total_loyalty_points", "loyalty_points"]) {
    const v = c && c[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "0.00";
};

export default function CartPanel({ onLineTap }) {
  const dispatch = useDispatch();
  const { lines, totals, couponTotal, customTotal, customer, isOutdoor, loyaltyAmount, storeCreditAmount, orderDiscounts, payments, giftCards } = useSelector((st) => st.webRegisterCart);
  // Android includeLineItemSection.ivDelete (clear customer): refused mid-split and while loyalty
  // points or store credit are applied ("Remove applied loyalty point or store credit first.").
  const removeCustomer = () => {
    if ((payments || []).length > 0) return ToastifyAlert("Split payment in progress", "error");
    if (Number(loyaltyAmount) !== 0 || Number(storeCreditAmount) !== 0)
      return ToastifyAlert("Remove applied loyalty point or store credit first.", "error");
    dispatch(setCustomer(null));
  };
  // Android register summary: in dual price the rows are the CARD columns (getCardSubTotal …).
  const disp = totals.display || null;
  const discount = disp ? Number(disp.orderDiscount) || 0 : (Number(couponTotal) || 0) + (Number(customTotal) || 0);
  const taxLines = disp && Array.isArray(disp.taxLines) ? disp.taxLines : totals.taxLines;
  const discountRows = Array.isArray(orderDiscounts) ? orderDiscounts : [];
  // Android ivRemoveDiscount: one ✕ removes EVERY order-level discount (custom / coupon / OTD);
  // refused once a split tender has been taken ("Split payment in progress").
  const removeDiscounts = () => {
    if ((payments || []).length > 0) return ToastifyAlert("Split payment in progress", "error");
    dispatch(clearOrderDiscount());
  };
  const itemCount = lines.reduce((s2, l) => s2 + (Number(l.orderCount) || 0), 0);

  /**
   * Should this line show the Mix & Match "Buy N get X off each" hint?
   *
   * Android fix (2026-07-31): the hint used to be decided purely from `model.bpId` — i.e. "this
   * PRODUCT takes part in a deal" — so after a `max_qty` cap the overflow units, which are correctly
   * left at FULL price, still advertised the deal. Decide from the LINE's state instead:
   *   • the line really got the bulk price            → show
   *   • the deal has not fired for ANY line of this group yet → show (a genuine "add one more" hint)
   *   • it fired but this line missed out (cap reached)       → hide
   */
  const showBulkPromo = (l) => {
    const bp = (l.model || {}).bpId;
    if (!bp) return false;
    if (l.isBulkPriceApplied) return true;
    return !lines.some((x) => (x.model || {}).bpId === bp && x.isBulkPriceApplied);
  };

  // Promotion text under the line name — mirrors Android tvPromotion: Mix&Match "Buy N get X Off
  // each" when the line has a bulk deal, else the BOGO deal message (isWarningShow).
  const promoText = (l) => {
    // QV-7124 rule 1/2: dezi wins, so a manufacturer-discounted line never advertises a BOGO or
    // Mix&Match deal — the deal was suppressed on it and showing the label would be a lie.
    if (l.isDeziUpcLine) return "";
    const m = l.model || {};
    if (m.bpId) {
      if (!showBulkPromo(l)) return l.isWarningShow || "";
      const pct = m.bulkIsPercentage === "1" || m.bulkIsPercentage === 1 || m.bulkIsPercentage === true;
      return pct ? `Buy ${m.bulkQuantity} get ${m.bulkPrice}% Off each` : `Buy ${m.bulkQuantity} get $${m.bulkPrice} Off each`;
    }
    return l.isWarningShow || "";
  };

  // QV-7124 rule 2 — the discount line under a manufacturer-discounted item reads "Manufacturer
  // Discount", never "Item Discount". Sound because manual discounts and item coupons are BLOCKED on
  // a dezi line, so its per-line discount can only be the manufacturer's.
  // Android NewCartAdapter.displayPrice: the line shows unitPrice × count (unit = override price
  // when set, else the base price) — NOT the penny-pass net (`jsonNewPrice`). That matters for a
  // lottery ticket, which is deliberately excluded from the penny/discount pass and therefore never
  // gets a jsonNewPrice (it showed $0.00). BOGO free row shows its own unit price; payout lines show
  // their amount negative. The item-level discount is a separate row underneath (tvDiscount).
  const lineDisplay = (l) => {
    const count = Number(l.orderCount) || 0;
    const unit = Number(l.orderUnitNewPrice) > 0 ? Number(l.orderUnitNewPrice) : Number(l.orderUnitPrice) || 0;
    const base = unit * count;
    const disc =
      Number(l.orderDiscount) > 0 ? Number(l.orderDiscount) * count : Number(l.orderDiscountPercent) > 0 ? ((unit * Number(l.orderDiscountPercent)) / 100) * count : 0;
    if (l.isLotteryPayout || l.isScratcherPayout) return { price: -(base - disc), discount: 0 };
    if (l.isBoGoProduct) return { price: Number(l.orderUnitPrice) || 0, discount: 0 };
    return { price: base, discount: disc };
  };
  const manufacturerText = (l) =>
    Number(l.deziItemDiscountAmt) > 0
      ? `Manufacturer Discount −${fmt(Number(l.deziItemDiscountAmt) * Number(l.orderCount))}`
      : "";

  // Android onDeleteClick/onOrderDelete guards: removing any item is blocked once a split payment
  // has started, or while an OTD price or a loyalty payment is applied (all computed against the
  // current cart — changing it mid-way would corrupt the taken tenders / distributed discount).
  const deleteBlocked = () => {
    if ((payments || []).length > 0) {
      ToastifyAlert("Split payment in progress", "error");
      return true;
    }
    if (isOutdoor) {
      ToastifyAlert("Remove the OTD price first", "error");
      return true;
    }
    if (Number(loyaltyAmount) > 0) {
      ToastifyAlert("Remove the loyalty payment first", "error");
      return true;
    }
    // A REDEEMED gift card (a tender, not a sold "Gift Card(...)" line) is applied against the
    // current cart total; deleting a line while it is still applied lets the redeemed amount exceed
    // the remaining total and drives the grand total negative (Android ae13e4cdf9).
    if ((giftCards || []).length > 0) {
      ToastifyAlert("Remove the gift card first", "error");
      return true;
    }
    return false;
  };

  // Android: an applied OTD price locks the per-line editor — tapping a line shows "remove OTD first"
  // instead of opening it (the OTD amount was distributed across the current lines, so editing one
  // line would corrupt it). `blocked` = the row types that never open the editor at all.
  const openLine = (l, blocked) => {
    if (!onLineTap || blocked) return;
    if (isOutdoor) return ToastifyAlert("Remove the OTD price first", "error");
    onLineTap(l);
  };

  return (
    <>
      <div className="qv-cart-head">
        <span className="qv-cart-title">Current sale</span>
        <span className="qv-cart-count qv-num">{itemCount} item{itemCount === 1 ? "" : "s"}</span>
      </div>
      {customer && (
        <div className="qv-cart-customer">
          <span aria-hidden="true">👤</span>
          {/* Android refreshLoyaltyProgram: etSearchCustomer = "$fullName [$total_bonus_points Points]" ("0.00" when absent). */}
          <span>
            {customer._name || `${customer.fname || ""} ${customer.lname || ""}`.trim() || "Customer"}
            {" "}
            <span className="qv-cart-customer-pts">[{customerPointsLabel(customer)} Points]</span>
          </span>
          <button type="button" className="qv-cust-x" onClick={removeCustomer} title="Remove customer" aria-label="Remove customer">✕</button>
        </div>
      )}

      {lines.length === 0 ? (
        <div className="qv-cart-list qv-empty">Scan or tap a product to start</div>
      ) : (
        <div className="qv-cart-list qv-scroll">
          {lines.map((l) => {
            // Android NewCartAdapter gating: a BOGO-discounted (free) row has NO delete option —
            // remove the paid row instead and the deal re-evaluates. A 100%-free BOGO row can't
            // open the editor either.
            const isBogoRow = !!l.isBoGoProduct;
            // Android NewCartAdapter: payout rows never open the editor and have no qty stepper (delete stays).
            const isPayoutRow = !!(l.isLotteryPayout || l.isScratcherPayout);
            // Gift-card and lottery-ticket lines are fixed-value: POS does NOT open the item editor on
            // them (no line discount / price override / tax — QA parity, Varun 2026-08-27). Their qty
            // stepper and delete ✕ stay available in the cart row; only the tap-to-edit is suppressed.
            const isGiftCardRow = !!l.isGiftCard;
            const isLotteryTicketRow = l.isLotteryTicket === "1";
            const editBlocked =
              (isBogoRow && Number(l.orderUnitPrice) === 0) || isPayoutRow || isGiftCardRow || isLotteryTicketRow;
            const promo = promoText(l);
            const manu = manufacturerText(l);
            return (
              <div key={l.orderId} className="qv-line">
                <div
                  className="qv-line-main"
                  role={onLineTap && !editBlocked ? "button" : undefined}
                  tabIndex={onLineTap && !editBlocked ? 0 : undefined}
                  onClick={() => openLine(l, editBlocked)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && onLineTap && !editBlocked) {
                      e.preventDefault();
                      openLine(l, editBlocked);
                    }
                  }}
                  style={onLineTap && !editBlocked ? undefined : { cursor: "default" }}
                >
                  {/* Android: title = "{count} x {name}"; below it the promotion text (if any). */}
                  <span className="qv-line-name"><span className="qv-num">{l.orderCount}</span> × {l.orderName}</span>
                  {promo && <span className="qv-line-note">{promo}</span>}
                  {manu && <span className="qv-line-note">{manu}</span>}
                  {!manu && lineDisplay(l).discount > 0 && (
                    <span className="qv-line-note">Discount −{fmt(lineDisplay(l).discount)}</span>
                  )}
                </div>

                {!isBogoRow && !isPayoutRow ? (
                  <span className="qv-stepper">
                    <button
                      className="qv-btn qv-step"
                      aria-label={`Decrease quantity of ${l.orderName}`}
                      onClick={() => {
                        // "−" to zero removes the row → same guards as delete (Android onOrderDelete).
                        if (l.orderCount - 1 <= 0 && deleteBlocked()) return;
                        dispatch(updateQty({ orderId: l.orderId, qty: l.orderCount - 1 }));
                      }}
                    >−</button>
                    <span className="qv-line-qty qv-num">{l.orderCount}</span>
                    <button
                      className="qv-btn qv-step"
                      aria-label={`Increase quantity of ${l.orderName}`}
                      onClick={() => {
                        // Per-unit rows: caps apply to TOTAL units of this product+variant across rows.
                        const units = lines
                          .filter((x) => x.orderProductId === l.orderProductId && (x.variantId || "") === (l.variantId || ""))
                          .reduce((s2, x) => s2 + (Number(x.orderCount) || 0), 0);
                        if (l.stockTracked && l.maxStock > 0 && units >= l.maxStock) {
                          ToastifyAlert("Reached maximum quantity", "error");
                          return;
                        }
                        if (Number(l.purchaseQtyCap) > 0 && units >= Number(l.purchaseQtyCap)) {
                          ToastifyAlert(`This product is limited to ${l.purchaseQtyCap} per transaction`, "error");
                          return;
                        }
                        dispatch(updateQty({ orderId: l.orderId, qty: l.orderCount + 1 }));
                      }}
                    >+</button>
                  </span>
                ) : isBogoRow ? (
                  <span className="qv-line-free">Free</span>
                ) : null}

                <span className="qv-line-end">
                  {/* Android amount_negative_value: "-$5.00" for payout rows, never "$-5.00" */}
                  <span className="qv-line-price qv-num">{lineDisplay(l).price < 0 ? `−${fmt(-lineDisplay(l).price)}` : fmt(lineDisplay(l).price)}</span>
                  {!isBogoRow && (
                    <button
                      className="qv-btn qv-btn--danger qv-line-del"
                      aria-label={`Remove ${l.orderName}`}
                      onClick={() => {
                        if (deleteBlocked()) return;
                        dispatch(removeItem(l.orderId));
                      }}
                    >✕</button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="qv-totals">
        <div className="qv-trow"><span>Subtotal</span><span className="qv-num">{fmt(disp ? disp.subtotal : totals.subtotal)}</span></div>
        {discount > 0 && (
          <div className="qv-trow qv-trow--discount">
            <span className="qv-disc-label">
              <button type="button" className="qv-disc-x" onClick={removeDiscounts} title="Remove discount" aria-label="Remove discount">✕</button>
              {discountRows.length === 1 ? `Discount${discountRows[0].isCoupon ? `(${discountRows[0].name})` : ""}` : "Discounts"}
            </span>
            <span className="qv-num">−{fmt(discount)}</span>
          </div>
        )}
        {/* Android cart shows every applied discount as its own green line: "Discount" / "Discount(NAME)" */}
        {discountRows.length > 1 &&
          discountRows.map((d, i) => (
            <div key={i} className="qv-trow qv-trow--discount qv-trow--sub">
              <span>Discount{d.isCoupon ? `(${d.name})` : ""}{d.isPercent ? " %" : ""}</span>
              <span className="qv-num">−{fmt(d.amount)}</span>
            </div>
          ))}
        {/* Per-tax breakdown (DefaultTax, State Tax, …) — mirrors the Android register. */}
        {Array.isArray(taxLines) && taxLines.length > 0 ? (
          taxLines.map((tx, i) => (
            <div key={i} className="qv-trow">
              <span>{tx.name}{tx.percent ? ` · ${fmtPct(tx.percentRaw, tx.percent)}%` : ""}</span>
              <span className="qv-num">{fmt(tx.amount)}</span>
            </div>
          ))
        ) : (
          totals.taxRateInAmount > 0 && <div className="qv-trow"><span>Tax</span><span className="qv-num">{fmt(totals.taxRateInAmount)}</span></div>
        )}
        {/* Android isTipShows() (7fc76c90c9): the tip collected on split card legs, between Tax and Food Stampable. */}
        {(payments || []).reduce((s, p) => s + (Number(p.tip) || 0), 0) !== 0 && (
          <div className="qv-trow"><span>Tip</span><span className="qv-num">{fmt((payments || []).reduce((s, p) => s + (Number(p.tip) || 0), 0))}</span></div>
        )}
        {/* Android isFoodStampableShows(): the EBT-eligible total (incl. its tax) whenever it is non-zero. */}
        {Number(totals.foodStampable) !== 0 && (
          <div className="qv-trow"><span>Food Stampable</span><span className="qv-num">{fmt(totals.foodStampable)}</span></div>
        )}
        {/* The one number the customer and the cashier both look at. Announced politely so screen
            readers hear the new total after each change rather than the whole cart. */}
        <div className="qv-grand">
          <span className="qv-grand-label">Total</span>
          <span className="qv-grand-value qv-num" aria-live="polite">{fmt(disp ? disp.total : totals.grandTotal)}</span>
        </div>
      </div>
    </>
  );
}
